import { createTheme } from "@mui/material/styles";
import Colors from "./Colors";

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: Colors.primary,
      light: Colors.primaryLight,
      dark: Colors.primaryDark,
      contrastText: Colors.bgPrimary,
    },
    secondary: {
      main: Colors.secondary,
      light: Colors.secondaryLight,
      dark: Colors.secondaryDark,
    },
    background: {
      default: Colors.bgPrimary,
      paper: Colors.bgSecondary,
    },
    text: {
      primary: Colors.textPrimary,
      secondary: Colors.textSecondary,
      disabled: Colors.textMuted,
    },
    divider: Colors.borderPrimary,
    success: {
      main: Colors.success,
      light: Colors.successLight,
    },
    error: {
      main: Colors.error,
      light: Colors.errorLight,
    },
    warning: {
      main: Colors.warning,
      light: Colors.warningLight,
    },
    info: {
      main: Colors.info,
      light: Colors.infoLight,
    },
  },

  shape: {
    borderRadius: 4,
  },

  spacing: 8,

  typography: {
    fontFamily: [
      '"Montserrat"',
      '"Inter"',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),

    h1: {
      fontSize: '2.5rem',
      fontWeight: 800,
      letterSpacing: '0',
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 800,
      letterSpacing: '0',
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 800,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.8125rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    button: {
      fontSize: '0.8rem',
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.4,
    },
  },

  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
      easeIn: "cubic-bezier(0.4, 0, 1, 1)",
      sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "#06040A",
          scrollbarColor: `${Colors.borderPrimary} ${Colors.bgSecondary}`,
          color: Colors.textPrimary,
        },
        '*::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '*::-webkit-scrollbar-track': {
          backgroundColor: Colors.bgSecondary,
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: Colors.borderPrimary,
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: Colors.textMuted,
          },
        },
        html: {
          scrollBehavior: "smooth",
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          padding: '5px 14px',
          fontWeight: 600,
          boxShadow: 'none',
          transition: 'background-color 0.15s ease, border-color 0.15s ease',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        sizeSmall: {
          padding: '3px 10px',
          fontSize: '0.72rem',
        },
        sizeLarge: {
          padding: '8px 20px',
        },
        contained: {
          '&:hover': { boxShadow: 'none' },
        },
        outlined: {
          borderWidth: '1px',
          '&:hover': {
            borderWidth: '1px',
            backgroundColor: Colors.bgTertiary,
          },
        },
        text: {
          '&:hover': {
            backgroundColor: Colors.bgTertiary,
          },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          color: Colors.white,
          borderRadius: 4,
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: Colors.bgTertiary,
            color: Colors.white,
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: Colors.bgSecondary,
          borderRadius: 8,
          border: `1px solid ${Colors.borderSecondary}`,
          boxShadow: 'none',
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
        },
        elevation2: {
          boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: Colors.secondary,
          borderRight: `1px solid ${Colors.borderPrimary}`,
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: Colors.secondary,
          borderBottom: `1px solid ${Colors.borderPrimary}`,
          boxShadow: 'none',
        },
      },
    },

    MuiDrawerToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: Colors.borderPrimary,
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': {
              borderColor: Colors.borderPrimary,
              transition: 'border-color 0.2s ease',
            },
            '&:hover fieldset': {
              borderColor: Colors.primary,
            },
            '&.Mui-focused fieldset': {
              borderColor: Colors.primary,
              borderWidth: '2px',
            },
          },
          '& .MuiInputBase-input': {
            color: Colors.textPrimary,
          },
          '& .MuiInputLabel-root': {
            color: Colors.textSecondary,
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        input: {
          padding: '14px 16px',
        },
        notchedOutline: {
          borderColor: Colors.borderPrimary,
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        icon: {
          color: Colors.white,
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: Colors.bgSecondary,
          border: `1px solid ${Colors.borderPrimary}`,
          borderRadius: 6,
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          margin: '1px 6px',
          padding: '7px 10px',
          fontSize: '0.82rem',
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: Colors.bgTertiary,
          },
          '&.Mui-selected': {
            backgroundColor: Colors.bgTertiary,
            '&:hover': {
              backgroundColor: Colors.bgElevated,
            },
          },
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          margin: '1px 6px',
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: Colors.bgTertiary,
          },
          '&.Mui-selected': {
            backgroundColor: Colors.bgTertiary,
            borderLeft: `3px solid ${Colors.primary}`,
          },
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: Colors.white,
          minWidth: 40,
        },
      },
    },

    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: Colors.textPrimary,
          fontWeight: 500,
        },
        secondary: {
          color: Colors.textSecondary,
        },
      },
    },

    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: Colors.white,
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid rgba(255,255,255,0.05)`,
          padding: '10px 14px',
          fontSize: '0.82rem',
          color: Colors.textPrimary,
        },
        head: {
          backgroundColor: Colors.tableHeader,
          fontWeight: 700,
          color: Colors.textSecondary,
          fontSize: '0.7rem',
          textTransform: 'uppercase',
          letterSpacing: '0.07em',
          padding: '10px 14px',
          borderBottom: `1px solid ${Colors.borderPrimary}`,
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          backgroundColor: Colors.tableBg,
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: Colors.tableHover,
          },
          '&:nth-of-type(even)': {
            backgroundColor: Colors.bgSecondary,
          },
        },
      },
    },

    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: Colors.tableBg,
          borderRadius: 6,
          border: `1px solid ${Colors.borderPrimary}`,
        },
      },
    },

    // ─── MUIDataTable overrides ───────────────────────────────────────────────
    MUIDataTableToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: Colors.bgSecondary,
          borderTopLeftRadius: 6,
          borderTopRightRadius: 6,
          padding: '8px 16px',
          borderBottom: `1px solid ${Colors.borderPrimary}`,
          '& [class*="MuiTypography"]': {
            color: Colors.textPrimary,
            fontWeight: 700,
            fontSize: '1rem',
          },
          '& .MuiIconButton-root': {
            color: Colors.textSecondary,
            '&:hover': { color: Colors.textPrimary, backgroundColor: Colors.bgTertiary },
          },
        },
      },
    },
    MUIDataTableSearch: {
      styleOverrides: {
        searchText: {
          '& .MuiInputBase-input': {
            color: Colors.textPrimary,
            '&::placeholder': { color: Colors.textSecondary, opacity: 1 },
          },
          '& .MuiInput-underline:before': { borderBottomColor: Colors.borderPrimary },
          '& .MuiInput-underline:hover:before': { borderBottomColor: Colors.primary },
          '& .MuiInput-underline:after': { borderBottomColor: Colors.primary },
        },
        clearIcon: { color: Colors.textSecondary },
        searchIcon: { color: Colors.textSecondary },
      },
    },
    MUIDataTableHeadCell: {
      styleOverrides: {
        root: {
          backgroundColor: `${Colors.bgTertiary} !important`,
          color: `${Colors.textSecondary} !important`,
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.07em',
          borderBottom: `2px solid ${Colors.borderPrimary}`,
          padding: '12px 16px',
          '& .MuiButtonBase-root': {
            color: Colors.textSecondary,
            '&:hover': { color: Colors.textPrimary },
            '& svg': { color: Colors.textSecondary },
          },
          '& span': { color: `${Colors.textSecondary} !important` },
        },
        fixedHeader: { backgroundColor: `${Colors.bgTertiary} !important` },
        contentWrapper: { color: `${Colors.textSecondary} !important` },
        sortActive: { color: `${Colors.primary} !important` },
      },
    },
    MUIDataTableBodyCell: {
      styleOverrides: {
        root: {
          color: Colors.textPrimary,
          fontSize: '0.82rem',
          padding: '10px 14px',
          borderBottom: `1px solid ${Colors.borderPrimary}`,
        },
      },
    },
    MUIDataTableBodyRow: {
      styleOverrides: {
        root: {
          backgroundColor: Colors.bgSecondary,
          transition: 'background-color 0.15s ease',
          '&:hover': { backgroundColor: Colors.bgTertiary },
          '&:nth-of-type(even)': { backgroundColor: Colors.bgPrimary },
          '&:nth-of-type(even):hover': { backgroundColor: Colors.bgTertiary },
        },
      },
    },
    MUIDataTableFilterList: {
      styleOverrides: {
        chip: { margin: '4px' },
      },
    },
    MUIDataTableFilter: {
      styleOverrides: {
        root: {
          backgroundColor: Colors.bgSecondary,
          padding: '20px',
          '& .MuiTypography-root': { color: Colors.textPrimary },
          '& .MuiInputLabel-root': { color: Colors.textSecondary },
          '& .MuiInput-underline:before': { borderBottomColor: Colors.borderPrimary },
          '& .MuiSelect-icon': { color: Colors.textSecondary },
        },
        header: { color: Colors.textPrimary, fontWeight: 700 },
        reset: {
          '& .MuiButton-root': { color: Colors.primary },
        },
      },
    },
    MUIDataTablePagination: {
      styleOverrides: {
        root: {
          borderTop: `1px solid ${Colors.borderPrimary}`,
          backgroundColor: Colors.bgSecondary,
          '& .MuiTablePagination-displayedRows': { color: Colors.textSecondary },
          '& .MuiTablePagination-selectLabel': { color: Colors.textSecondary },
          '& .MuiTablePagination-select': { color: Colors.textPrimary },
          '& .MuiIconButton-root': {
            color: Colors.textSecondary,
            '&:hover': { color: Colors.textPrimary, backgroundColor: Colors.bgTertiary },
            '&.Mui-disabled': { color: Colors.textMuted },
          },
        },
      },
    },
    MUIDataTableSelectCell: {
      styleOverrides: {
        root: { backgroundColor: Colors.bgSecondary },
        headerCell: { backgroundColor: `${Colors.bgTertiary} !important` },
      },
    },
    MUIDataTableJumpToPage: {
      styleOverrides: {
        root: { color: Colors.textSecondary },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: Colors.bgSecondary,
          backgroundImage: 'none',
          borderRadius: 8,
          border: `1px solid ${Colors.borderPrimary}`,
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          fontWeight: 700,
          padding: '18px 20px 12px',
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '12px 20px',
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 20px 18px',
          gap: '8px',
        },
      },
    },

    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: Colors.bgSecondary,
          border: `1px solid ${Colors.borderSecondary}`,
          borderRadius: '6px !important',
          backgroundImage: 'none',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: '6px 0',
          },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          minHeight: '44px',
          '&:hover': {
            backgroundColor: Colors.bgTertiary,
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
        filled: {
          backgroundColor: Colors.bgTertiary,
        },
        outlined: {
          borderColor: Colors.borderPrimary,
        },
      },
    },

    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: Colors.bgTertiary,
          color: Colors.textPrimary,
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: Colors.bgElevated,
          color: Colors.textPrimary,
          fontSize: '0.75rem',
          borderRadius: 6,
          padding: '8px 12px',
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          marginLeft: '4px',
          marginTop: '6px',
        },
      },
    },

    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '48px !important',
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
        standardSuccess: {
          backgroundColor: `${Colors.success}20`,
          color: Colors.successLight,
        },
        standardError: {
          backgroundColor: `${Colors.error}20`,
          color: Colors.errorLight,
        },
        standardWarning: {
          backgroundColor: `${Colors.warning}20`,
          color: Colors.warningLight,
        },
        standardInfo: {
          backgroundColor: `${Colors.info}20`,
          color: Colors.infoLight,
        },
      },
    },
  },
});

export default darkTheme;
